import React, { useState, useEffect, useCallback } from 'react';

interface Job {
    id: string;
    jobId: string;
    executionId: string;
    stepNumber: number;
    type: string;
    status: string;
    gbqJobId?: string;
    startedAt?: string;
    completedAt?: string;
    errorMessage?: string;
    parameters?: any;
}

interface ExecutionJobsListProps {
    executionId: string;
    refreshTrigger?: number;
}

const ExecutionJobsList: React.FC<ExecutionJobsListProps> = ({ executionId, refreshTrigger }) => {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);

    // Helper function to format job dates, handling various formats
    const formatJobDate = (date: string | Date | undefined | null): string => {
        if (!date) return '-';
        try {
            // Handle Firestore Timestamp objects (if they come through as objects with seconds/nanoseconds)
            if (typeof date === 'object' && 'seconds' in date && 'nanoseconds' in date) {
                const timestamp = (date as any).seconds * 1000 + (date as any).nanoseconds / 1000000;
                return new Date(timestamp).toLocaleString();
            }
            // Handle Date objects or ISO strings
            const dateObj = date instanceof Date ? date : new Date(date);
            if (isNaN(dateObj.getTime())) {
                return '-';
            }
            return dateObj.toLocaleString();
        } catch (error) {
            console.error('Error formatting date:', error, date);
            return '-';
        }
    };

    const loadJobs = useCallback(async () => {
        try {
            const res = await fetch(`/api/jobs?executionId=${executionId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('id_token')}` }
            });
            if (res.ok) {
                setJobs(await res.json());
            }
        } catch (error) {
            console.error('Failed to load jobs:', error);
        } finally {
            setLoading(false);
        }
    }, [executionId]);

    useEffect(() => {
        loadJobs();
    }, [loadJobs, refreshTrigger]);

    const getJobStatusClass = (status: string) => {
        const statusLower = status.toLowerCase();
        if (statusLower === 'completed') return 'job-status-completed';
        if (statusLower === 'failed' || statusLower === 'error') return 'job-status-error';
        if (statusLower === 'running') return 'job-status-running';
        if (statusLower === 'pending' || statusLower === 'waiting') return 'job-status-pending';
        if (statusLower === 'cancelled') return 'job-status-cancelled';
        return 'job-status-default';
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <span>Loading jobs...</span>
            </div>
        );
    }

    if (jobs.length === 0) {
        return (
            <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">No jobs found</div>
                <div className="empty-state-text">No jobs have been created for this execution yet.</div>
            </div>
        );
    }

    return (
        <div className="execution-jobs-list">
            <h4>Execution Jobs</h4>
            <table className="jobs-table">
                <thead>
                    <tr>
                        <th>Step</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>GBQ Job ID</th>
                        <th>Started</th>
                        <th>Completed</th>
                    </tr>
                </thead>
                <tbody>
                    {jobs.map(job => {
                        return (
                            <React.Fragment key={job.id}>
                                <tr style={{ borderBottom: job.errorMessage ? 'none' : undefined }}>
                                    <td>{job.stepNumber}</td>
                                    <td>{job.type}</td>
                                    <td>
                                        <span className={`job-status-badge ${getJobStatusClass(job.status)}`}>
                                            {job.status}
                                        </span>
                                    </td>
                                    <td className="job-id-cell">{job.gbqJobId || '-'}</td>
                                    <td>{formatJobDate(job.startedAt)}</td>
                                    <td>{formatJobDate(job.completedAt)}</td>
                                </tr>
                                {job.errorMessage && (
                                    <tr className="job-error-row">
                                        <td colSpan={6}>
                                            <span className="error-label">Error:</span>
                                            <span className="error-message">{job.errorMessage}</span>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ExecutionJobsList;
